import { useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Shared Tag-Listen-Loader. Lädt `GET /api/tags` (volle, alphabetische Tag-Liste,
 * lowercase-normalisiert) für die Tag-Filter-Combobox im FilterBar.
 *
 * Shared-Module-Store wie `use-vocab`: Single-Flight (erster Subscriber lädt, der
 * Rest reused), Cache überlebt Navigation. Consumer abonnieren via
 * `useSyncExternalStore` (tear-frei). Tri-State-Discriminated-Union konsistent
 * mit `VocabState`/`StatsState`.
 *
 * Invalidation: Tags ändern sich nur über den Admin-TagManager. `invalidateTags()`
 * nach Mutationen aufrufen (gleiche Semantik wie `invalidateVocab`). Für die
 * Filter-Combobox bei 3–5 internen Usern unkritisch — eine frisch angelegte
 * Vokabel taucht spätestens beim nächsten Mount auf.
 */
export type TagsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; tags: string[] };

const INITIAL: TagsState = { status: 'loading' };

let store: TagsState = INITIAL;
let inFlight: AbortController | null = null;
let started = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setStore(next: TagsState) {
  store = next;
  emit();
}

function load() {
  inFlight?.abort();
  const ctrl = new AbortController();
  inFlight = ctrl;
  started = true;
  setStore({ status: 'loading' });
  void (async () => {
    try {
      const res = await api<{ tags: string[] }>('/api/tags', { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      inFlight = null;
      setStore({ status: 'ok', tags: res.tags });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      inFlight = null;
      setStore({ status: 'error' });
    }
  })();
}

function ensureLoaded() {
  if (started) return;
  load();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  ensureLoaded();
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): TagsState {
  return store;
}

/** Verwirft den geteilten Cache + refetcht (nach Tag-Mutationen im Admin). */
export function invalidateTags(): void {
  if (listeners.size === 0) {
    inFlight?.abort();
    inFlight = null;
    started = false;
    store = INITIAL;
    return;
  }
  load();
}

/** Test-only: Module-Store auf Initialzustand zurücksetzen. */
export function __resetTagsForTest(): void {
  inFlight?.abort();
  inFlight = null;
  started = false;
  store = INITIAL;
  listeners.clear();
}

export function useTags(): { state: TagsState; refetch: () => void } {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { state, refetch: load };
}
